import { useRef, useCallback, useEffect } from 'react';

interface SoundConfig {
  volume?: number;
  playbackRate?: number;
  loop?: boolean;
}

export const SOUNDS = {
  correct: '/sounds/correct.mp3',
  incorrect: '/sounds/incorrect.mp3',
  celebration: '/sounds/celebration.mp3',
  complete: '/sounds/complete.mp3',
  victory: '/sounds/celebration.mp3', // Use celebration as fallback
  click: '/sounds/correct.mp3' // Use correct as fallback
} as const;

export type SoundType = keyof typeof SOUNDS;

export const useSound = () => {
  const audioRefs = useRef<Record<SoundType, HTMLAudioElement>>({} as any);
  const isMuted = useRef(false);

  // Preload all sounds and check accessibility preferences
  useEffect(() => {
    // Check for reduced motion preference (accessibility)
    const prefersSilence = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersSilence) {
      isMuted.current = true;
      localStorage.setItem('sounds-muted', 'true');
    }
    
    // Load mute preference from localStorage
    const savedMute = localStorage.getItem('sounds-muted');
    if (savedMute === 'true') {
      isMuted.current = true;
    }

    // Preload all sounds with error handling
    Object.entries(SOUNDS).forEach(([key, url]) => {
      const audio = new Audio(url);
      audio.preload = 'auto';
      
      // Add error handler for failed loads
      audio.addEventListener('error', (e) => {
        console.warn(`Failed to load sound: ${key}`, e);
      });
      
      // Add load success handler
      audio.addEventListener('canplaythrough', () => {
        console.log(`✅ Sound loaded: ${key}`);
      }, { once: true });
      
      audioRefs.current[key as SoundType] = audio;
    });

    // Cleanup
    return () => {
      Object.values(audioRefs.current).forEach(audio => {
        audio.pause();
        audio.src = '';
      });
    };
  }, []);

  const playSound = useCallback((
    soundType: SoundType,
    config: SoundConfig = {}
  ) => {
    if (isMuted.current) return;

    const audio = audioRefs.current[soundType];
    if (!audio) {
      console.warn(`Sound not found: ${soundType}`);
      return;
    }

    // Check if audio is ready to play
    if (audio.readyState < 2) {
      console.warn(`Sound not ready yet: ${soundType}`);
      return;
    }

    try {
      // Reset audio to start
      audio.currentTime = 0;
      audio.volume = config.volume ?? 0.5;
      audio.playbackRate = config.playbackRate ?? 1;
      audio.loop = config.loop ?? false;

      // Play sound with promise handling
      const playPromise = audio.play();
      
      if (playPromise !== undefined) {
        playPromise.catch(err => {
          console.warn(`Sound play failed for ${soundType}:`, err.message);
        });
      }
    } catch (err: any) {
      console.warn(`Error playing sound ${soundType}:`, err.message);
    }
  }, []);

  const toggleMute = useCallback(() => {
    isMuted.current = !isMuted.current;
    // Persist mute preference
    localStorage.setItem('sounds-muted', String(isMuted.current));
    return isMuted.current;
  }, []);

  const stopSound = useCallback((soundType: SoundType) => {
    const audio = audioRefs.current[soundType];
    if (audio) {
      audio.pause();
      audio.currentTime = 0;
    }
  }, []);

  return {
    playSound,
    toggleMute,
    stopSound,
    isMuted: isMuted.current
  };
};
